import { useState, useRef } from 'react'
import { useTheme } from '../context/ThemeContext'

function DraggableQueue({ queue = [], onReorder, onAddNew }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const [items, setItems] = useState(queue)
    const [dragIdx, setDragIdx] = useState(null)
    const [overIdx, setOverIdx] = useState(null)
    const [saving, setSaving] = useState(false)
    const dragItem = useRef(null)

    // Sync if parent passes new queue
    if (queue !== items && queue.length > 0 && !saving) {
        const queueIds = queue.map(q => q.id).join(',')
        const itemIds = items.map(q => q.id).join(',')
        if (queueIds !== itemIds || items.length === 0) {
            setItems(queue)
        }
    }

    const handleDragStart = (e, idx) => {
        dragItem.current = idx
        setDragIdx(idx)
        e.dataTransfer.effectAllowed = 'all'
        // Set data for cross-component drops (queue → Gantt)
        const item = items[idx]
        e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'queue-item', item }))
    }

    const handleDragOver = (e, idx) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setOverIdx(idx)
    }

    const handleDrop = async (e, dropIdx) => {
        e.preventDefault()
        const fromIdx = dragItem.current
        if (fromIdx === null || fromIdx === dropIdx) {
            setDragIdx(null)
            setOverIdx(null)
            return
        }

        const updated = [...items]
        const [moved] = updated.splice(fromIdx, 1)
        updated.splice(dropIdx, 0, moved)

        const reordered = updated.map((item, i) => ({ ...item, position: i + 1 }))
        setItems(reordered)
        setDragIdx(null)
        setOverIdx(null)

        if (onReorder) {
            setSaving(true)
            try {
                await onReorder(reordered.map(q => ({ id: q.id, position: q.position })))
            } catch (err) {
                setItems(queue)
            } finally {
                setSaving(false)
            }
        }
    }

    const handleDragEnd = () => {
        setDragIdx(null)
        setOverIdx(null)
    }

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
                            <th className={`text-left py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'} w-8`}>#</th>
                            <th className={`text-left py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'} w-6`}></th>
                            <th className={`text-left py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Well</th>
                            <th className={`text-left py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Work Type</th>
                            <th className={`text-left py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Reason</th>
                            <th className={`text-right py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>BOPD</th>
                            <th className={`text-right py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Days Down</th>
                            <th className={`text-right py-2 px-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Est. Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((q, idx) => (
                            <tr
                                key={q.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, idx)}
                                onDragOver={(e) => handleDragOver(e, idx)}
                                onDrop={(e) => handleDrop(e, idx)}
                                onDragEnd={handleDragEnd}
                                className={`border-b transition-colors ${
                                    dragIdx === idx
                                        ? 'opacity-40'
                                        : overIdx === idx
                                            ? isDark ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-300'
                                            : isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-gray-200 hover:bg-gray-50'
                                }`}
                                style={{ cursor: 'grab' }}
                            >
                                <td className={`py-2.5 px-2 font-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {q.position}
                                </td>
                                <td className="py-2.5 px-2">
                                    <svg className={`w-4 h-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
                                    </svg>
                                </td>
                                <td className={`py-2.5 px-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                    {q.wellName || q.well}
                                </td>
                                <td className={`py-2.5 px-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {q.workType || '--'}
                                </td>
                                <td className={`py-2.5 px-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {q.reason || '--'}
                                </td>
                                <td className={`py-2.5 px-2 text-right font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {q.bopdImpact || '--'}
                                </td>
                                <td className={`py-2.5 px-2 text-right font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {q.daysDown || '--'}
                                </td>
                                <td className={`py-2.5 px-2 text-right font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {q.costEstimate ? `$${q.costEstimate.toLocaleString()}` : '--'}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={8} className={`py-6 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    No wells currently down
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {saving && (
                <div className={`text-xs mt-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    Saving...
                </div>
            )}
        </div>
    )
}

export default DraggableQueue
